import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useLang, useCart, useSettings, cldUrl, fmtDzd, productPriceDzd } from "../lib/providers";
import { useCustomer } from "../lib/customerAuth";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { ShoppingBag, Globe, Search, X, User, Menu, PackageSearch } from "lucide-react";

export default function Header() {
  const { lang, t, toggle } = useLang();
  const { count, setOpen, open: cartOpen, items: cartItems } = useCart();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { settings } = useSettings();
  const { user: customer } = useCustomer();
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const dispatchSearch = (value) => {
    setQ(value);
    window.dispatchEvent(new CustomEvent("global-search", { detail: { q: value } }));
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const el = document.querySelector("#products");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const closeMobileNav = () => setMobileNavOpen(false);
  const goToAnchor = (hash) => {
    closeMobileNav();
    // Ensure we're on home for anchors
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    } else {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-themed-deep/80 border-b border-white/5"
      data-testid="site-header"
    >
      <div className="max-w-7xl w-full mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20 gap-2 sm:gap-3 flex-nowrap">
        <a href="#top" className="flex items-center gap-3 flex-1 md:flex-none min-w-0 md:min-w-fit overflow-hidden md:overflow-visible" data-testid="logo-link">
          <img
            src={cldUrl(settings.logo_url, { w: 240 })}
            alt={settings.site_name}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            style={{
              height: `${settings.logo_size_px || 48}px`,
              opacity: (settings.logo_opacity ?? 100) / 100,
              borderRadius: settings.logo_shape === "circle" ? "50%" : settings.logo_shape === "rounded" ? "8px" : "0",
              objectFit: "cover",
            }}
            className="w-auto"
            data-testid="header-logo"
          />
          <div className="hidden lg:block">
            <span className="block font-heading font-black uppercase tracking-tight text-sm leading-tight text-white">
              {settings.site_name}
            </span>
            {(lang === "ar" ? settings.header_badge_text_ar : settings.header_badge_text_fr) && (
              <span className="text-[9px] tracking-widest uppercase text-neon font-bold">
                {lang === "ar" ? settings.header_badge_text_ar : settings.header_badge_text_fr}
              </span>
            )}
          </div>
        </a>

        {/* Header search bar (desktop inline, mobile collapsible) */}
        <form onSubmit={submitSearch} className="flex-1 min-w-[280px] max-w-md lg:max-w-2xl xl:max-w-3xl mx-2 hidden md:block">
          <div className="relative">
            <Search className="w-5 h-5 absolute start-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={(e) => dispatchSearch(e.target.value)}
              placeholder={lang === "ar" ? "ابحث عن منتج..." : "Rechercher un produit…"}
              className="w-full bg-black/40 border border-white/10 text-white text-base ps-10 pe-10 py-2.5 outline-none focus:border-neon transition-colors"
              data-testid="header-search-input"
            />
            {q && (
              <button type="button" onClick={() => dispatchSearch("")} className="absolute end-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white" aria-label="clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </form>

        <nav className="hidden xl:flex items-center gap-6 text-xs font-bold tracking-[0.2em] uppercase text-white/70 shrink-0">
          <a href="#products" className="hover:text-neon transition-colors" data-testid="nav-products">{t.nav.products}</a>
          <a href="#about" className="hover:text-neon transition-colors" data-testid="nav-about">{t.nav.about}</a>
          <a href="#contact" className="hover:text-neon transition-colors" data-testid="nav-contact">{t.nav.contact}</a>
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 flex-nowrap">
          {/* Mobile hamburger (< xl) */}
          <button
            onClick={() => setMobileNavOpen((o) => !o)}
            className="xl:hidden flex items-center justify-center w-9 h-9 border border-white/10 hover:border-neon hover:text-neon transition-colors shrink-0"
            data-testid="mobile-nav-toggle"
            aria-label="menu"
            aria-expanded={mobileNavOpen}
          >
            {mobileNavOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
          {/* Mobile search toggle */}
          <button
            onClick={() => setSearchOpen((o) => !o)}
            className="md:hidden flex items-center justify-center w-9 h-9 border border-white/10 hover:border-neon hover:text-neon transition-colors shrink-0"
            data-testid="mobile-search-toggle"
            aria-label="search"
          >
            <Search className="w-4 h-4" />
          </button>
          {/* DZ Shopper — demande produit introuvable/alternative/suggestion, page dédiée. Style accent + libellé compréhensible : court sur mobile, complet sur desktop large. */}
          <button
            onClick={() => navigate("/dz-shopper")}
            className="flex items-center gap-1.5 h-9 px-2.5 sm:px-3 border border-neon/50 bg-neon/10 text-neon hover:bg-neon hover:text-black transition-colors shrink-0"
            data-testid="product-request-trigger-btn"
            aria-label="DZ Shopper"
            title={lang === "ar" ? "🔎 تبحث عن منتج؟" : "🔎 Vous cherchez un produit ?"}
          >
            <PackageSearch className="w-4 h-4 shrink-0" />
            <span className="hidden lg:inline text-[11px] font-black uppercase tracking-widest whitespace-nowrap">
              {lang === "ar" ? "🔎 تبحث عن منتج؟" : "🔎 Vous cherchez un produit ?"}
            </span>
            <span className="inline lg:hidden text-[11px] font-black uppercase tracking-widest whitespace-nowrap">
              {lang === "ar" ? "منتج؟" : "Produit ?"}
            </span>
          </button>
          {/* Payment logos: desktop/tablet only, hidden on mobile <768px to save horizontal space */}
          {settings.payment_floater_enabled !== false && (
            <div className="hidden md:flex">
              <HeaderPaymentLogos settings={settings} />
            </div>
          )}
          <button
            onClick={toggle}
            className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-2 border border-white/10 hover:border-neon hover:text-neon transition-colors text-xs font-bold tracking-widest uppercase shrink-0"
            data-testid="lang-switcher"
          >
            <Globe className="w-3.5 h-3.5" />
            {lang === "fr" ? "AR" : "FR"}
          </button>
          {settings.customer_accounts_enabled !== false && (
            <button
              onClick={() => navigate("/mon-compte")}
              className="relative flex flex-col items-center justify-center gap-0.5 h-11 px-2 sm:px-4 border border-white/10 hover:border-neon hover:text-neon transition-colors shrink-0"
              aria-label="account"
              data-testid="header-account-btn"
              title={customer ? customer.name : (lang === "ar" ? "حسابي" : "Mon compte")}
            >
              <User className="w-4 h-4" />
              <span className="text-[9px] font-black uppercase tracking-widest leading-none">
                {lang === "ar" ? "حسابي" : "Mon compte"}
              </span>
              {customer && <span className="absolute top-1 end-1 w-2 h-2 bg-neon rounded-full" />}
            </button>
          )}
          <Button
            onClick={() => { setOpen(true); navigate("/panier"); }}
            className="relative bg-neon text-black hover:opacity-90 rounded-none font-black uppercase tracking-widest text-xs px-2.5 sm:px-4 py-2 h-auto shrink-0"
            data-testid="open-cart-btn"
          >
            <ShoppingBag className="w-4 h-4 sm:me-2" />
            <span className="hidden sm:inline">{t.nav.cart}</span>
            {count > 0 && (
              <span className="ms-1.5 sm:ms-2 bg-black text-neon text-[10px] min-w-[20px] h-5 px-1 flex items-center justify-center font-black">
                {count}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Mobile nav drawer (< xl) */}
      {mobileNavOpen && (
        <div
          className="xl:hidden border-t border-white/5 bg-themed-deep/95 backdrop-blur-xl"
          data-testid="mobile-nav-drawer"
        >
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-1">
            <button
              onClick={() => goToAnchor("#products")}
              className="text-start py-3 px-3 border-b border-white/5 text-xs font-bold tracking-[0.2em] uppercase text-white/80 hover:text-neon hover:bg-white/5 transition-colors"
              data-testid="mobile-nav-products"
            >
              {t.nav.products}
            </button>
            <button
              onClick={() => goToAnchor("#about")}
              className="text-start py-3 px-3 border-b border-white/5 text-xs font-bold tracking-[0.2em] uppercase text-white/80 hover:text-neon hover:bg-white/5 transition-colors"
              data-testid="mobile-nav-about"
            >
              {t.nav.about}
            </button>
            <button
              onClick={() => goToAnchor("#contact")}
              className="text-start py-3 px-3 border-b border-white/5 text-xs font-bold tracking-[0.2em] uppercase text-white/80 hover:text-neon hover:bg-white/5 transition-colors"
              data-testid="mobile-nav-contact"
            >
              {t.nav.contact}
            </button>
            <button
              onClick={() => { closeMobileNav(); navigate("/avis"); }}
              className="text-start py-3 px-3 text-xs font-bold tracking-[0.2em] uppercase text-white/80 hover:text-neon hover:bg-white/5 transition-colors"
              data-testid="mobile-nav-reviews"
            >
              {lang === "ar" ? "الآراء" : "Avis clients"}
            </button>
          </nav>
        </div>
      )}

      {/* Mobile search bar (collapsible) */}
      {searchOpen && (
        <form onSubmit={submitSearch} className="md:hidden border-t border-white/5 bg-themed-deep/95 backdrop-blur-xl px-4 py-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            <input
              type="text"
              autoFocus
              value={q}
              onChange={(e) => dispatchSearch(e.target.value)}
              placeholder={lang === "ar" ? "ابحث عن منتج..." : "Rechercher un produit…"}
              className="w-full bg-black/40 border border-white/10 text-white text-sm ps-9 pe-9 py-2 outline-none focus:border-neon"
              data-testid="header-search-input-mobile"
            />
            {q && (
              <button type="button" onClick={() => dispatchSearch("")} className="absolute end-2 top-1/2 -translate-y-1/2 text-white/40" aria-label="clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </form>
      )}
      {/* Barre panier toujours visible (mobile + ordinateur) : nombre d'articles + total, un clic ouvre le panier.
          Rendue dans document.body : le header a backdrop-blur, qui piègerait un enfant position:fixed. */}
      {count > 0 && !cartOpen && pathname !== "/panier" && createPortal(
        <div className="dzts-floating-cart fixed bottom-0 inset-x-0 z-[60] px-3 pt-2 bg-gradient-to-t from-black via-black/90 to-transparent" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <button
            onClick={() => { setOpen(true); navigate("/panier"); }}
            className="w-full max-w-lg mx-auto flex items-center justify-between bg-neon text-black h-14 px-4 font-black shadow-2xl shadow-neon/30 active:scale-[0.98] transition-transform"
            aria-label={lang === "ar" ? "عرض السلة" : "Voir mon panier"}
            data-testid="mobile-floating-cart-btn"
          >
            <span className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" />
              <span>{lang === "ar" ? "عرض السلة" : "Voir mon panier"}</span>
              <span className="bg-black text-neon text-xs min-w-[22px] h-[22px] px-1 flex items-center justify-center rounded-full">{count}</span>
            </span>
            <span className="text-base">{fmtDzd(cartItems.reduce((sum, it) => sum + productPriceDzd(it, settings) * it.qty, 0))}</span>
          </button>
        </div>,
        document.body
      )}
    </header>
  );
}

function HeaderPaymentLogos({ settings }) {
  // Collect logos from payment_methods (enabled + with logo) and online payment_providers
  const standard = (settings.payment_methods || [])
    .filter((p) => p.enabled && p.logo_url)
    .map((p) => ({ key: `m-${p.id}`, logo: p.logo_url, alt: p.label_fr || p.id }));
  const online = Object.entries(settings.payment_providers || {})
    .filter(([, cfg]) => cfg?.enabled && cfg?.logo_url)
    // Provider name is intentionally NEVER exposed to the customer — only generic CIB / Edahabia label.
    .map(([name, cfg]) => ({ key: `o-${name}`, logo: cfg.logo_url, alt: "Paiement par Carte CIB / Edahabia" }));
  const all = [...standard, ...online];
  if (all.length === 0) return null;
  return (
    <div className="flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 h-7 sm:h-9 border border-white/10" data-testid="header-payment-logos">
      {all.slice(0, 3).map((p) => (
        <img
          key={p.key}
          src={cldUrl(p.logo, { w: 120 })}
          alt={p.alt}
          title={p.alt}
          style={{ height: `${settings.payment_logos_height_px || 20}px` }}
          className="w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
          loading="lazy"
          decoding="async"
        />
      ))}
    </div>
  );
}
